/**
 * Live activity: the job queue as it moves. The top bar shows what is running (with a pulse);
 * clicking it opens the activity drawer. While anything is queued or running the queue is polled
 * every few seconds, otherwise every 10 s. When a job finishes, a toast says what it did and every
 * screen's data is refreshed, so numbers update without a reload.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Drawer } from './Drawer';
import { StatusPill, type StatusTone } from './StatusPill';
import { queryClient, useSb } from '../lib/query';
import { supabase } from '../lib/supabase';
import { ago, fmtDate, humanize } from '../lib/format';
import { jobState, kindMeta, type JobRow } from '../lib/queries/jobs';

const FAST_MS = 2_500;
const SLOW_MS = 10_000;
const TOAST_MS = 6_000;

function useActivity(brandId: string | null) {
  const [fast, setFast] = useState(false);
  const q = useSb(
    ['seo', 'jobs', 'activity', brandId ?? 'all'],
    async () => {
      let r = supabase.from('jobs').select('id, brand_id, kind, status, created_at, finished_at, result, error, attempts');
      if (brandId) r = r.eq('brand_id', brandId);
      const res = await r.order('created_at', { ascending: false }).limit(40);
      return { data: (res.data ?? []) as JobRow[], error: res.error };
    },
    { refetchInterval: fast ? FAST_MS : SLOW_MS },
  );
  const rows = useMemo(() => q.data ?? [], [q.data]);
  const live = rows.filter((j) => j.status === 'queued' || j.status === 'running');
  useEffect(() => setFast(live.length > 0), [live.length]);
  return { rows, live, q };
}

/** A short line from a job's result: up to three numbers, e.g. "26 drafts · 2 inserted · 1 alerts". */
export function resultLine(result: unknown): string {
  if (!result || typeof result !== 'object' || Array.isArray(result)) return '';
  const r = result as Record<string, unknown>;
  if (typeof r.reason === 'string') return humanize(r.reason);
  return Object.entries(r)
    .filter(([, v]) => typeof v === 'number')
    .slice(0, 3)
    .map(([k, v]) => `${(v as number).toLocaleString('en-US')} ${humanize(k).toLowerCase()}`)
    .join(' · ');
}

function duration(j: JobRow): string {
  if (!j.finished_at || !j.created_at) return '';
  const s = Math.max(0, Math.round((Date.parse(j.finished_at) - Date.parse(j.created_at)) / 1000));
  return s < 90 ? `${s} s` : `${Math.round(s / 60)} min`;
}

type Toast = { id: string; title: string; body: string; tone: StatusTone };

export function LiveActivity({ brandId }: { brandId: string | null }) {
  const { rows, live, q } = useActivity(brandId);
  const [open, setOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seen = useRef<Map<string, string> | null>(null);

  // Toast and refresh when a job we saw queued or running finishes.
  useEffect(() => {
    if (!q.data) return;
    const prev = seen.current;
    const next = new Map(rows.map((j) => [j.id, j.status]));
    seen.current = next;
    if (!prev) return;
    const finished = rows.filter((j) => {
      const was = prev.get(j.id);
      return (was === 'queued' || was === 'running') && j.status !== 'queued' && j.status !== 'running';
    });
    if (!finished.length) return;
    void queryClient.invalidateQueries({ predicate: (qq) => qq.queryKey[0] === 'seo' && !(qq.queryKey[1] === 'jobs' && qq.queryKey[2] === 'activity') });
    const add = finished.map<Toast>((j) => {
      const st = jobState(j);
      return { id: `${j.id}:${j.status}`, title: `${kindMeta(j.kind).label}: ${st.label.toLowerCase()}`, body: resultLine(j.result) || st.note || '', tone: st.tone };
    });
    setToasts((t) => [...t, ...add].slice(-4));
    for (const t of add) window.setTimeout(() => setToasts((all) => all.filter((x) => x.id !== t.id)), TOAST_MS);
  }, [q.data, rows]);

  const running = live.filter((j) => j.status === 'running');
  const label = running.length
    ? running.length === 1
      ? `${kindMeta(running[0].kind).label} running`
      : `${running.length} jobs running`
    : live.length
      ? `${live.length} queued`
      : 'Activity';

  return (
    <>
      <button type="button" className={`pill live-pill${live.length ? ' on' : ''}`} onClick={() => setOpen(true)} aria-haspopup="dialog" title="Jobs and data collection">
        <span className={`dot${live.length ? ' live' : ' idle'}`} aria-hidden="true" />
        {label}
      </button>
      <Drawer open={open} onClose={() => setOpen(false)} title="Activity" sub="Data collection and engine runs, newest first. Updates live.">
        {live.length ? (
          <div className="act-now" aria-live="polite">
            {live.map((j) => (
              <div key={j.id} className="act-run">
                <div className="ar-h">
                  <b>{kindMeta(j.kind).label}</b>
                  <span>{j.status === 'running' ? `started ${ago(j.created_at)}` : 'waiting for a worker'}</span>
                </div>
                <div className={`ar-bar${j.status === 'queued' ? ' q' : ''}`} aria-hidden="true">
                  <i />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="mut" style={{ margin: 0, fontSize: 13 }}>
            Nothing is running. The next scheduled step is shown on the dashboard.
          </p>
        )}
        <ul className="act-list" aria-label="Recent jobs">
          {rows
            .filter((j) => j.status !== 'queued' && j.status !== 'running')
            .map((j) => {
              const st = jobState(j);
              return (
                <li key={j.id}>
                  <StatusPill tone={st.tone} label={st.label} />
                  <span className="al-k">
                    <b>{kindMeta(j.kind).label}</b>
                    <small>{resultLine(j.result) || st.note || kindMeta(j.kind).schedule}</small>
                  </span>
                  <span className="al-t" title={fmtDate(j.finished_at ?? j.created_at, { time: true })}>
                    {ago(j.finished_at ?? j.created_at)}
                    {duration(j) ? <small>{duration(j)}</small> : null}
                  </span>
                </li>
              );
            })}
        </ul>
      </Drawer>
      <div className="toasts" aria-live="polite" role="status">
        {toasts.map((t) => (
          <div key={t.id} className={`toast t-${t.tone}`}>
            <b>{t.title}</b>
            {t.body ? <span>{t.body}</span> : null}
          </div>
        ))}
      </div>
    </>
  );
}
